import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { filterUsers } from '../lib/utils';
import UserDetailsDialog from '../components/UserDetailsDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Users, Search, Shield, ShieldOff, UserCheck, UserX, Eye, EyeOff, Plus, MapPin, CalendarClock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { t, language } = useLanguage();
  const { isViewer } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // The row whose details dialog is open, or null. Holding the user object rather
  // than an id means the dialog renders the exact row that was clicked and never
  // has to look it up again.
  const [detailsUser, setDetailsUser] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressUser, setAddressUser] = useState(null);
  const [addressForm, setAddressForm] = useState({ address: '', city: '', county: '' });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    company_name: '',
    cnp_cui: '',
    license_plate: '',
    brand: '',
    model: '',
    address: '',
    city: '',
    county: '',
  });

  const resetForm = () => setFormData({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    company_name: '',
    cnp_cui: '',
    license_plate: '',
    brand: '',
    model: '',
    address: '',
    city: '',
    county: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-admin`);
      toast.success(t('user_updated'));
      fetchUsers();
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleToggleViewer = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-viewer`);
      toast.success(t('user_updated'));
      fetchUsers();
    } catch (error) {
      console.error('Error toggling viewer:', error);
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-active`);
      toast.success(t('user_updated'));
      fetchUsers();
    } catch (error) {
      console.error('Error toggling active:', error);
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleToggleSharedQrMonth = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-shared-qr-month`);
      toast.success(res.data?.message || t('user_updated'));
      fetchUsers();
    } catch (error) {
      console.error('Error toggling shared QR month validity:', error);
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success(t('user_created'));
      setAddDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error creating user');
    }
  };

  const openAddressDialog = (user) => {
    setAddressUser(user);
    setAddressForm({ address: user.address || '', city: user.city || '', county: user.county || '' });
    setAddressDialogOpen(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${addressUser.id}/address`, addressForm);
      toast.success(t('user_updated'));
      setAddressDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating address');
    }
  };

  // Shared with the add-subscription picker so the dashboard has one search
  // behaviour rather than two that drift. This one also folds diacritics and
  // matches the combined "first last" string, so it returns strictly more than
  // the filter it replaces. Staff and deactivated accounts still appear here:
  // filterUsers only decides who matches, never who is eligible to be listed.
  const filteredUsers = filterUsers(users, searchQuery);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('users')}</h1>
          <p className="text-muted-foreground mt-1">
            {users.length} {t('total_users').toLowerCase()}
          </p>
        </div>
        <Button onClick={() => { resetForm(); setAddDialogOpen(true); }} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('add_user')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-users"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredUsers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('first_name')}</TableHead>
                  <TableHead>{t('last_name')}</TableHead>
                  <TableHead>{t('email')}</TableHead>
                  <TableHead>{t('phone')}</TableHead>
                  <TableHead>{t('company_name')}</TableHead>
                  <TableHead>{t('is_admin')}</TableHead>
                  <TableHead>{t('viewer')}</TableHead>
                  <TableHead>{t('is_active')}</TableHead>
                  <TableHead>{t('referrals_count')}</TableHead>
                  {!isViewer && <TableHead className="text-right">{t('actions')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {isViewer ? (
                          <Users className="w-4 h-4 text-primary" />
                        ) : (
                          // Button, not a raw <button>: the four action controls on
                          // this same row (toggle admin, toggle viewer, toggle active,
                          // edit address) are all `Button variant="ghost"`, and a
                          // hand-rolled element here would differ in focus ring,
                          // disabled styling and SVG sizing on a page that is
                          // otherwise completely consistent. `size="icon"` exists for
                          // exactly this case. Admin-only because the endpoint behind
                          // it refuses operators — the UI must not offer a 403.
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setDetailsUser(user)}
                            title={t('user_details')}
                            data-testid={`user-details-btn-${user.id}`}
                          >
                            <Users className="w-4 h-4 text-primary" />
                          </Button>
                        )}
                        {user.first_name}
                      </div>
                    </TableCell>
                    <TableCell>{user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{user.company_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={user.is_admin ? 'default' : 'secondary'}>
                        {user.is_admin ? t('yes') : t('no')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_viewer ? 'outline' : 'secondary'}>
                        {user.is_viewer ? t('yes') : t('no')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'destructive'}>
                        {user.is_active ? t('active') : t('inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {/* Absent on every account created before the referral migration,
                          and on any account the backfill has not reached yet — the
                          backend already normalises it to 0, this only covers a row that
                          arrives without the field at all. */}
                      {user.successful_referrals > 0 ? (
                        <span className="font-medium">{user.successful_referrals}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    {!isViewer && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAdmin(user.id)}
                            title={t('toggle_admin')}
                            data-testid={`toggle-admin-${user.id}`}
                          >
                            {user.is_admin ? (
                              <ShieldOff className="w-4 h-4 text-amber-500" />
                            ) : (
                              <Shield className="w-4 h-4 text-primary" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleViewer(user.id)}
                            title={user.is_viewer ? 'Revocă acces viewer' : 'Acordă acces viewer'}
                            data-testid={`toggle-viewer-${user.id}`}
                          >
                            {user.is_viewer ? (
                              <EyeOff className="w-4 h-4 text-amber-500" />
                            ) : (
                              <Eye className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(user.id)}
                            title={t('toggle_active')}
                            data-testid={`toggle-active-${user.id}`}
                          >
                            {user.is_active ? (
                              <UserX className="w-4 h-4 text-destructive" />
                            ) : (
                              <UserCheck className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleSharedQrMonth(user.id)}
                            title={user.shared_qr_until_month_end ? t('toggle_shared_qr_month_on') : t('toggle_shared_qr_month_off')}
                            aria-pressed={!!user.shared_qr_until_month_end}
                            data-testid={`toggle-shared-qr-month-${user.id}`}
                          >
                            <CalendarClock className={`w-4 h-4 ${user.shared_qr_until_month_end ? 'text-primary' : 'text-muted-foreground'}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAddressDialog(user)}
                            title={t('edit_address')}
                            data-testid={`edit-address-${user.id}`}
                          >
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mb-4 opacity-50" />
              <p>{t('no_users')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>{t('add_user')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-first-name">{t('first_name')} *</Label>
                  <Input
                    id="add-first-name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="add-user-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-last-name">{t('last_name')} *</Label>
                  <Input
                    id="add-last-name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="add-user-last-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-email">{t('email')} *</Label>
                <Input
                  id="add-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="add-user-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-phone">{t('phone')} *</Label>
                  <Input
                    id="add-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="add-user-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-password">{t('password')} *</Label>
                  <Input
                    id="add-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="add-user-password"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-license-plate">{t('license_plate')}</Label>
                  <Input
                    id="add-license-plate"
                    value={formData.license_plate}
                    onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                    data-testid="add-user-license-plate"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-brand">{t('brand')}</Label>
                  <Input
                    id="add-brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    data-testid="add-user-brand"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-model">{t('model')}</Label>
                  <Input
                    id="add-model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    data-testid="add-user-model"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-company">{t('company_name')}</Label>
                  <Input
                    id="add-company"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    data-testid="add-user-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-cnp-cui">{t('cnp_cui')}</Label>
                  <Input
                    id="add-cnp-cui"
                    value={formData.cnp_cui}
                    onChange={(e) => setFormData({ ...formData, cnp_cui: e.target.value })}
                    data-testid="add-user-cnp-cui"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-address">{t('address')}</Label>
                <Input
                  id="add-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="add-user-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-city">{t('city')}</Label>
                  <Input
                    id="add-city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    data-testid="add-user-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-county">{t('county')}</Label>
                  <Input
                    id="add-county"
                    value={formData.county}
                    onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                    data-testid="add-user-county"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="save-user-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-address-dialog">
          <DialogHeader>
            <DialogTitle>{t('edit_address')} — {addressUser?.first_name} {addressUser?.last_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveAddress}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-address">{t('address')}</Label>
                <Input
                  id="edit-address"
                  value={addressForm.address}
                  onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                  data-testid="edit-address-street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-city">{t('city')}</Label>
                  <Input
                    id="edit-city"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                    data-testid="edit-address-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-county">{t('county')}</Label>
                  <Input
                    id="edit-county"
                    value={addressForm.county}
                    onChange={(e) => setAddressForm({ ...addressForm, county: e.target.value })}
                    data-testid="edit-address-county"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddressDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="save-address-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <UserDetailsDialog
        user={detailsUser}
        open={detailsUser !== null}
        onOpenChange={(next) => { if (!next) setDetailsUser(null); }}
      />
    </div>
  );
};

export default UsersPage;
